import cpuIcon from '@/assets/cpu.svg';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '@/components/ui/card';
import { Handle, Position, useNodeId } from '@xyflow/react';
import { useCallback, useState, useEffect } from 'react';
import NodeToolbar from '../NodeToolbar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkflowsService, UpdateWorkflowNodeRequest } from '@/api';
import { toast } from 'sonner';

const handleStyle = { left: 10 };

export default function ProcessorNode({
  setNodes,
  workflowId,
  selected,
  nodeName,
}: {
  setNodes: any;
  workflowId: string;
  selected: boolean;
  nodeName: string;
}) {
  // Automatically get the current node's ID
  const nodeId = useNodeId();

  // Determine the initial name: if nodeName starts with "Node-", use default "Run Analyzer"
  const initialName = (nodeName && !nodeName.startsWith("Node-")) ? nodeName : "Run Analyzer";

  // Local state for the node title, initialized from initialName
  const [name, setName] = useState(initialName);
  const queryClient = useQueryClient();

  // Ensure local state updates when nodeName prop changes (e.g. on refresh)
  useEffect(() => {
    const updatedName = (nodeName && !nodeName.startsWith("Node-")) ? nodeName : "Run Analyzer";
    setName(updatedName);
  }, [nodeName]);

  const updateNode = useMutation({
    mutationFn: async (payload: { req: UpdateWorkflowNodeRequest; nodeId: string }) => {
      console.log('Updating node with payload:', payload);
      const res = await WorkflowsService.updateWorkflowNode(
        workflowId,
        payload.nodeId,
        payload.req,
      );
      return res;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
      toast('Node name updated successfully.', {
        duration: 5000,
      });
    },
    onError: (error: any) => {
      console.error('API Error:', error);
      toast('An error occurred :: Unable to update node name.', {
        description: error?.message || 'Unknown error',
        duration: 5000,
      });
    },
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleNameBlur = () => {
    if (!name?.trim()) {
      toast('Node name cannot be empty.', { duration: 5000 });
      return;
    }

    if (!nodeId) {
      console.error('Node ID is undefined. Cannot update the node name.');
      toast('An error occurred :: Node ID is missing.', { duration: 5000 });
      return;
    }

    if (name !== initialName) {
      updateNode.mutateAsync({
        req: { name },
        nodeId,
      });
    }
  };

  return (
    <Card
      className={`w-[200px] rounded-md shadow-none transition-all duration-200
      ${selected ? 'border-2 border-blue-500' : 'border'}
      hover:shadow-md hover:border-blue-500 relative`}    >

      <Handle type="target" position={Position.Top} />
      <NodeToolbar setNodes={setNodes} workflowId={workflowId} />
      <CardContent className="flex h-full w-full flex-1 items-center justify-start p-2">
        <Avatar className="h-6 w-6">
          <AvatarImage src={cpuIcon} className="bg-muted p-[6px]" />
          <AvatarFallback>Z</AvatarFallback>
        </Avatar>
        <div className="ml-2 flex flex-col">
          <CardTitle className="text-[8px] w-[140px]">
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              className="w-full border-none bg-transparent focus:outline-none"
            />
          </CardTitle>
          <CardDescription className="max-w-lg text-balance text-[8px] leading-relaxed">
            Process data
          </CardDescription>
        </div>
      </CardContent>

      <Handle type="source" position={Position.Bottom} id="a" />
    </Card>
  );
}
